import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import {
  fetchAllAchievements,
  fetchEarnConfig,
  updateFeaturedAchievements,
  AchievementType,
  EarnedAchievement,
  LockedAchievement,
} from '@/redux/thunks/achievementThunks';
import {
  selectEarnConfig,
  selectFullAchievements,
  selectFullAchievementsLoading,
  selectSavingFeatured,
} from '@/redux/selectors/achievementSelectors';
import { formatEarnAmount } from '@/utils/achievementEarn';
import AchievementDetailModal from './AchievementDetailModal';
import './page.scss';

const MAX_FEATURED = 4;
const EXPLORER_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://kleverscan.org/transaction'
    : 'https://testnet.kleverscan.org/transaction';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

const rarityClass = (pct: number) => {
  if (pct <= 5) return 'ach-card__rarity--mythic';
  if (pct <= 25) return 'ach-card__rarity--rare';
  return '';
};

const formatRarity = (pct: number) => {
  if (pct === 0) return '—';
  if (pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
};

interface CardProps {
  achievement: EarnedAchievement;
  draggable: boolean;
  dragging?: boolean;
  earnDisplay?: string | null;
  onClick?: (a: EarnedAchievement) => void;
}

const EarnedCard: React.FC<CardProps> = ({ achievement, draggable, dragging, earnDisplay, onClick }) => (
  <div
    className={[
      'ach-card',
      draggable ? 'ach-card--draggable' : '',
      dragging ? 'ach-card--dragging' : '',
      onClick ? 'ach-card--clickable' : '',
    ].filter(Boolean).join(' ')}
    title={achievement.description}
    onClick={onClick ? () => onClick(achievement) : undefined}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(achievement);
            }
          }
        : undefined
    }
  >
    <span className={`ach-card__rarity ${rarityClass(achievement.rarityPct)}`}>
      {formatRarity(achievement.rarityPct)}
    </span>
    <span className="ach-card__icon">{achievement.icon}</span>
    <span className="ach-card__name">{achievement.name}</span>
    {earnDisplay && <span className="ach-card__earn">{earnDisplay}</span>}
    <span className="ach-card__date">{formatDate(achievement.awardedAt)}</span>
    {achievement.txHash && (
      <a
        className="ach-card__onchain"
        href={`${EXPLORER_BASE}/${achievement.txHash}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        ✓ on-chain
      </a>
    )}
  </div>
);

interface SortableCardProps {
  achievement: EarnedAchievement;
  earnDisplay?: string | null;
  onSelect: (a: EarnedAchievement) => void;
}

const SortableCard: React.FC<SortableCardProps> = ({ achievement, earnDisplay, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: achievement.type });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Distinguish a click from a drag: if pointer moves > 5px between down/up,
  // treat as a drag and don't open the modal.
  const downPos = React.useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    downPos.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: React.PointerEvent) => {
    const start = downPos.current;
    downPos.current = null;
    if (!start || isDragging) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.hypot(dx, dy) <= 5) {
      onSelect(achievement);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      <EarnedCard
        achievement={achievement}
        draggable
        dragging={isDragging}
        earnDisplay={earnDisplay}
      />
    </div>
  );
};

interface LockedCardProps {
  achievement: LockedAchievement;
  earnDisplay?: string | null;
  onClick: (a: LockedAchievement) => void;
}

const LockedCard: React.FC<LockedCardProps> = ({ achievement, earnDisplay, onClick }) => (
  <div
    className="ach-card ach-card--locked ach-card--clickable"
    title={achievement.howTo}
    role="button"
    tabIndex={0}
    onClick={() => onClick(achievement)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(achievement);
      }
    }}
  >
    <span className={`ach-card__rarity ${rarityClass(achievement.rarityPct)}`}>
      {formatRarity(achievement.rarityPct)}
    </span>
    <span className="ach-card__lock">🔒</span>
    <span className="ach-card__icon">{achievement.icon}</span>
    <span className="ach-card__name">{achievement.name}</span>
    {earnDisplay && <span className="ach-card__earn ach-card__earn--locked">{earnDisplay}</span>}
    <span className="ach-card__desc">{achievement.howTo}</span>
  </div>
);

const AchievementsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { username: routeUsername } = useParams<{ username?: string }>();
  const currentUser = useAppSelector((s) => s.auth.user);

  // Normalize: the route may include a leading "@" but Redux/API keys are bare usernames.
  const rawUsername = routeUsername ?? currentUser?.username;
  const username = rawUsername?.startsWith('@') ? rawUsername.slice(1) : rawUsername;

  // The PUT endpoint requires a valid JWT — without one, drag would 401 the backend.
  // Gate owner-mode on both Redux user state AND a present token so a stale Redux
  // state (e.g. logout in another tab) doesn't trigger an unauthorized request.
  const hasAccessToken =
    typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  const isOwner =
    hasAccessToken &&
    !!currentUser?.username &&
    (!routeUsername || username === currentUser.username);

  const data = useAppSelector((s) => (username ? selectFullAchievements(username)(s) : undefined));
  const loading = useAppSelector((s) =>
    username ? selectFullAchievementsLoading(username)(s) : false
  );
  const saving = useAppSelector(selectSavingFeatured);
  const earnConfig = useAppSelector(selectEarnConfig);

  const earnDisplayFor = (type: string): string | null =>
    formatEarnAmount(earnConfig, type);

  // Local ordering: first MAX_FEATURED items are featured, rest are earned-but-unpinned.
  const [order, setOrder] = useState<AchievementType[]>([]);

  // Active achievement to show in the detail modal (earned OR locked).
  const [selected, setSelected] = useState<EarnedAchievement | LockedAchievement | null>(null);

  useEffect(() => {
    if (username) {
      dispatch(fetchAllAchievements(username));
    }
  }, [dispatch, username]);

  // Load the public earn catalog once per page view. Cheap single
  // call, cached in Redux, drives the earn-amount badge on cards +
  // the modal row.
  useEffect(() => {
    if (!earnConfig) {
      dispatch(fetchEarnConfig());
    }
  }, [dispatch, earnConfig]);

  useEffect(() => {
    if (!data) return;
    const featured = data.earned
      .filter((a) => a.featuredOrder !== null)
      .sort((a, b) => (a.featuredOrder ?? 99) - (b.featuredOrder ?? 99))
      .map((a) => a.type);
    const rest = data.earned
      .filter((a) => a.featuredOrder === null)
      .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
      .map((a) => a.type);
    setOrder([...featured, ...rest]);
  }, [data]);

  const byType = useMemo(() => {
    const map = new Map<AchievementType, EarnedAchievement>();
    data?.earned.forEach((a) => map.set(a.type, a));
    return map;
  }, [data]);

  const sensors = useSensors(
    // Mouse: tiny distance threshold so clicks on the on-chain link still work.
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Touch: a 200ms long-press activates drag — normal swipes still scroll the page.
    // tolerance lets the finger jiggle a few px during the press without aborting.
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id as AchievementType);
    const newIndex = order.indexOf(over.id as AchievementType);
    if (oldIndex < 0 || newIndex < 0) return;

    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);

    const nextFeatured = next.slice(0, MAX_FEATURED);
    if (!username) return; // owner-mode is gated above; safety belt for TS
    dispatch(updateFeaturedAchievements({ username, featured: nextFeatured }));
  };

  if (!username) {
    return <div className="achievements-page__empty">Sign in to view your achievements.</div>;
  }

  if (loading && !data) {
    return <div className="achievements-page__loading">Loading achievements…</div>;
  }

  if (!data) {
    return <div className="achievements-page__empty">No achievements found.</div>;
  }

  const featuredTypes = order.slice(0, MAX_FEATURED);
  const earnedRestTypes = order.slice(MAX_FEATURED);
  const emptyFeaturedSlots = Math.max(0, MAX_FEATURED - featuredTypes.length);

  const featuredCards = featuredTypes
    .map((t) => byType.get(t))
    .filter((a): a is EarnedAchievement => !!a);
  const earnedRestCards = earnedRestTypes
    .map((t) => byType.get(t))
    .filter((a): a is EarnedAchievement => !!a);

  return (
    <div className="achievements-page">
      <header className="achievements-page__header">
        <h1 className="achievements-page__title">
          {(() => {
            // Prefer display name (e.g. "Joe Smith"), fall back to the
            // system-generated username with the @-prefix.
            const displayName = data.user?.displayName?.trim();
            const handle = data.user?.username ?? username;
            const label = displayName || `@${handle}`;
            return isOwner ? 'Your Achievements' : `${label}'s Achievements`;
          })()}
        </h1>
        <p className="achievements-page__subtitle">
          {isOwner
            ? 'Drag (or long-press on mobile) an earned achievement into the featured row to pin it on your profile.'
            : `Earned ${data.stats.unlocked} of ${data.stats.total} achievements.`}
        </p>
        <span className="achievements-page__stats">
          🏆 {data.stats.unlocked} of {data.stats.total} unlocked
          {saving && ' · saving…'}
        </span>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={isOwner ? handleDragEnd : undefined}
      >
        <SortableContext items={order} strategy={rectSortingStrategy}>
          <section className="achievements-page__section">
            <div className="achievements-page__section-header">
              <h2 className="achievements-page__section-title">Featured</h2>
              <span className="achievements-page__section-hint">
                {isOwner ? 'Shown on your profile page · up to 4' : 'Shown on profile'}
              </span>
            </div>
            <div className="achievements-page__featured-row">
              {featuredCards.map((a) =>
                isOwner ? (
                  <SortableCard key={a.type} achievement={a} earnDisplay={earnDisplayFor(a.type)} onSelect={setSelected} />
                ) : (
                  <EarnedCard key={a.type} achievement={a} draggable={false} earnDisplay={earnDisplayFor(a.type)} onClick={setSelected} />
                )
              )}
              {Array.from({ length: emptyFeaturedSlots }).map((_, i) => (
                <div key={`slot-${i}`} className="achievements-page__slot">
                  {isOwner ? 'Drag here to pin' : 'Empty'}
                </div>
              ))}
            </div>
          </section>

          {earnedRestCards.length > 0 && (
            <section className="achievements-page__section">
              <div className="achievements-page__section-header">
                <h2 className="achievements-page__section-title">Earned</h2>
                {isOwner && (
                  <span className="achievements-page__section-hint">
                    Drag into a featured slot to pin
                  </span>
                )}
              </div>
              <div className="achievements-page__grid">
                {earnedRestCards.map((a) =>
                  isOwner ? (
                    <SortableCard key={a.type} achievement={a} earnDisplay={earnDisplayFor(a.type)} onSelect={setSelected} />
                  ) : (
                    <EarnedCard key={a.type} achievement={a} draggable={false} earnDisplay={earnDisplayFor(a.type)} onClick={setSelected} />
                  )
                )}
              </div>
            </section>
          )}
        </SortableContext>
      </DndContext>

      {data.locked.length > 0 && (
        <section className="achievements-page__section">
          <div className="achievements-page__section-header">
            <h2 className="achievements-page__section-title">Locked</h2>
            <span className="achievements-page__section-hint">How to earn each</span>
          </div>
          {/* Group locked achievements by category — same order as
              data.categories so the sections cascade in intent order
              (early access → firsts → reputation → ...). Categories
              with zero locked items are omitted. */}
          {data.categories.map((cat) => {
            const inGroup = data.locked.filter((a) => a.category === cat.id);
            if (inGroup.length === 0) return null;
            return (
              <div key={cat.id} className="achievements-page__category">
                <div className="achievements-page__category-header">
                  <h3 className="achievements-page__category-title">{cat.label}</h3>
                  <span className="achievements-page__category-blurb">{cat.blurb}</span>
                </div>
                <div className="achievements-page__grid">
                  {inGroup.map((a) => (
                    <LockedCard
                      key={a.type}
                      achievement={a}
                      earnDisplay={earnDisplayFor(a.type)}
                      onClick={setSelected}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}

      <div className="achievements-page__footer">
        <Link
          to={isOwner ? `/profile/@${currentUser?.username ?? ''}` : `/profile/@${username}`}
          className="achievements-page__back-link"
        >
          <span className="achievements-page__back-link__arrow">←</span>
          Back to {isOwner ? 'your profile' : 'profile'}
        </Link>
      </div>

      <AchievementDetailModal
        achievement={selected}
        earnDisplay={selected ? earnDisplayFor(selected.type) : null}
        onClose={() => setSelected(null)}
        explorerBaseUrl={EXPLORER_BASE}
      />
    </div>
  );
};

export default AchievementsPage;
